/**
 * Cache of materials via their local ID
 */
export class AP214MaterialCache {
    constructor() {
        this.cache_ = new Map();
        this.assignments_ = new Map();
        this.relMaterialsMap = new Map();
        this.materialDefinitionsMap = new Map();
        this.styledItemMap = new Map();
    }
    /**
     * Get the number of materials in this.
     *
     * @return {number} The number of materials in this.
     */
    get size() {
        return this.cache_.size;
    }
    /**
     * Get the materials in the cache.
     *
     * @return {IterableIterator<[number, CanonicalMaterial]>} The iterator for the
     * local IDs and their respective materials.
     */
    [Symbol.iterator]() {
        return this.cache_[Symbol.iterator]();
    }
    /**
     * Get the materials in the cache (values only).
     *
     * @return {IterableIterator<CanonicalMaterial>} The iterator for the respective
     * materials.
     */
    materials() {
        return this.cache_.values();
    }
    /**
     * Add a material to the cache
     *
     * @param localID The local ID of the source material object
     * @param material The canonical material version of the material to add
     */
    add(localID, material) {
        this.cache_.set(localID, material);
    }
    /**
     * Get a material by its local ID.
     *
     * @param localID The local ID to fetch a material for.
     * @return {CanonicalMaterial | undefined} The material for the matching local ID
     */
    get(localID) {
        return this.cache_.get(localID);
    }
    /**
     * Get a material by its local ID.
     *
     * @param geometryLocalID The local ID of the geometry to fetch
     * @return {[CanonicalMaterial, number] | undefined} A tuple containing the
     * material and its id, or undefined if it is not found.
     */
    getMaterialIDByGeometryID(geometryLocalID) {
        return this.assignments_.get(geometryLocalID) ?? this.defaultMaterialLocalID;
    }
    /**
     * Assign a particular geometry to a particular material
     *
     * @param geometryLocalID The geometry
     * @param materialLocalID The material
     */
    addGeometryMapping(geometryLocalID, materialLocalID) {
        this.assignments_.set(geometryLocalID, materialLocalID);
    }
    /**
     * Map the current geometry to the current default material, if one is set.
     *
     * @param geometryLocalID The geometry ID to add.
     */
    addDefaultGeometryMapping(geometryLocalID) {
        const defaultMaterialLocalID = this.defaultMaterialLocalID;
        if (defaultMaterialLocalID !== void 0) {
            this.addGeometryMapping(geometryLocalID, defaultMaterialLocalID);
        }
    }
    /**
     * Get a material by its local geometry ID.
     *
     * @param geometryLocalID The local ID of the geometry to get the associated
     * material for.
     * @return {[CanonicalMaterial, number] | undefined} A tuple containing the
     * material and its id, or undefined if it is not found.
     */
    getMaterialByGeometryID(geometryLocalID) {
        const materialID = this.getMaterialIDByGeometryID(geometryLocalID) ?? this.defaultMaterialLocalID;
        if (materialID === void 0) {
            return;
        }
        const material = this.get(materialID);
        if (material === void 0) {
            return;
        }
        return [material, materialID];
    }
    /**
     * Get the material for a geometry scene node from the scene with these materials.
     *
     * @param node The scene geometry node.
     * @return {CanonicalMaterial | undefined} The canonical material associated with the geometry or
     * undefined if none is available.
     */
    getMaterialFromGeometryNode(node) {
        const geometryLocalID = (node.materialOverideLocalID ?? node.localID);
        const materialID = this.assignments_.get(geometryLocalID) ?? this.defaultMaterialLocalID;
        if (materialID === void 0) {
            return void 0;
        }
        const material = this.get(materialID);
        if (material === void 0) {
            return void 0;
        }
        return material;
    }
}
